import React, { useMemo } from 'react';
import T from 'prop-types';
import cx from 'classnames';

import SwitchButtons from './SwitchButtons';
import SimpleDropdown from './Dropdowns/SimpleDropdown';
import { appStore } from '../utils/helpers';

const singleStores = ['itunes_connect', 'google_play'];

const iconsByStore = {
  itunes_connect: 'hicon-apple',
  google_play: 'hicon-play',
  unified: 'hicon-unified',
};

const labelsByStore = {
  itunes_connect: 'iOS',
  google_play: 'Google Play',
  unified: 'Unified',
};

const emptyObject = {};

const SwitchStores = ({
  size,
  active,
  disabled,
  unifiedKey = 'both',
  withUnified = false,
  customParams = emptyObject,
  btnClassName,
  containerClassName = 'switch-stores-group',
  onChange,
}) => {
  const options = useMemo(() => {
    let stores = singleStores;
    if (withUnified) stores = [unifiedKey, ...stores];

    return stores.map(store => ({
      value: store,
      text: '',
      iconClass: iconsByStore[appStore(store)],
      ...(customParams[appStore(store)] || {}),
    }));
  }, [withUnified, unifiedKey, customParams]);

  return (
    <SwitchButtons
      size="unset"
      active={active}
      options={options}
      disabled={disabled}
      btnClassName={cx('switch-stores-btn hms-btn hms-btn--square hms-btn--secondary', size && `hms-btn--${size}`, btnClassName)}
      containerClassName={containerClassName}
      onChange={onChange}
    />
  );
};

SwitchStores.propTypes = {
  size: T.string,
  active: T.string.isRequired,
  disabled: T.bool,
  unifiedKey: T.string,
  withUnified: T.bool,
  customParams: T.shape({
    itunes_connect: T.object,
    google_play: T.object,
    unified: T.object,
  }),
  btnClassName: T.string,
  containerClassName: T.string,
  onChange: T.func.isRequired,
};

export default SwitchStores;

export const StoreDropdown = ({
  active,
  disabled,
  unifiedKey = 'unified',
  withUnified = false,
  size,
  variant = 'light',
  menuAlign,
  menuWidth = 238,
  isIconButton = false,
  customParams = emptyObject,
  onChange,
}) => {
  const options = useMemo(() => {
    let stores = singleStores;
    if (withUnified) stores = [unifiedKey, ...stores];

    return stores.map((store) => {
      const storeIcon = <i className={iconsByStore[appStore(store)]} />;
      const { labelText, ...restCustomParams } = customParams[appStore(store)] || {};

      return {
        value: store,
        label: (
          <div className="store-dropdown__label">
            {storeIcon}
            <span className="store-dropdown__label-text">
              {labelText || labelsByStore[appStore(store)]}
            </span>
          </div>
        ),
        // Fallback to 'label' if 'buttonLabel' is not provided
        buttonLabel: isIconButton ? storeIcon : null,
        className: 'menu-item--14',
        ...restCustomParams,
      };
    });
  }, [withUnified, unifiedKey, isIconButton, customParams]);

  return (
    <div className="store-dropdown">
      <SimpleDropdown
        size={size}
        caret={!isIconButton}
        shape={isIconButton ? 'square' : undefined}
        variant={variant}
        menuWidth={menuWidth}
        menuAlign={menuAlign}
        selected={active}
        disabled={disabled}
        options={options}
        isActiveSelectable={false}
        className="store-dropdown-menu"
        onChange={onChange}
      />
    </div>
  );
};

StoreDropdown.propTypes = {
  active: T.string.isRequired,
  disabled: T.bool,
  unifiedKey: T.string,
  withUnified: T.bool,
  size: T.string,
  variant: T.string,
  menuAlign: T.string,
  menuWidth: T.number,
  isIconButton: T.bool,
  customParams: T.shape({
    itunes_connect: T.object,
    google_play: T.object,
    unified: T.object,
  }),
  onChange: T.func.isRequired,
};
